import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  FormControlLabel,
  Grid,
  Popper,
  Stack,
  Switch,
  TextField,
  Typography
} from "@mui/material";
import axios from "axios";
import { useContext, useEffect, useState } from "react";
import { SearchesContext } from "../contexts/SearchesContext";
import { Contact } from "../orderEntry/types/Contact";
import SearchItems from "./SearchItems";
import OrderProductType from "./OrderProductType";
import { Preview } from "./types/Preview";
import { produce } from "immer";
import SentSearchEmailAlert from "./SentSearchEmailAlert";
import Names from "./names/Names";
import { OtherNameSearchTypes } from "./names/OtherNameSearchTypes";
import Properties from "./properties/Properties";
import Attachments from "./attachments/SearchAttachments";
import { logEvent } from "../utils/analyticsLogger";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import PhonebookContactAutocomplete from "../phonebook/phonebookAutocomplete/PhonebookContactAutocomplete";
import { ContactOption } from "../phonebook/phonebookAutocomplete/ContactOption";
import { IntelSearchGuidelineNote } from "./types/IntelSearchGuidelineNote";
import { IntelSearchGuideline } from "./types/IntelSearchGuideline";
import SearchGuidelinesTable from "./SearchGuidelinesTable";
import useResponsive from "../../minimals/hooks/useResponsive";

type SearchProp = {
  index: number;
};

export default function SearchCard({ index }: SearchProp) {
  const {
    orderNumber,
    setVendor,
    searchSections,
    setSearchSections,
    setPreview,
    sentEmail,
    notes,
    order,
    underwriter,
    hasUnsavedProductType,
    isSavingProductType
  } = useContext(SearchesContext);
  const isMobile = useResponsive("down", "md");
  const [intelStateVendors, setIntelStateVendors] = useState<Contact[]>([]);
  const [onlyIntelStateVendors, setOnlyIntelStateVendors] = useState<boolean>(true);
  const [intelSearchGuidelineNotes, setIntelSearchGuidelineNotes] = useState<IntelSearchGuidelineNote[]>([]);
  const [intelUnderwriterGuidelines, setIntelUnderwriterGuidelines] = useState<IntelSearchGuideline | null>(null);
  const searches = searchSections[index].searchRequests;
  const vendor = searchSections[index].vendor;

  const convertToSelectContact = (contact: Contact | null) => {
    if (!contact) return null;
    const selectContact = {
      lookupCode: contact.lookupCode,
      title: contact.title,
      firstName: contact.firstName,
      middleName: contact.middleName,
      lastName: contact.lastName,
      name: contact.name,
      email: contact.email,
      phone: contact.phone,
      extension: contact.ext,
      fax: contact.fax,
      cell: contact.cell,
      company: contact.company,
      payeeName: contact.payeeName,
      isMarketingSource: false,
      address1: contact.address1,
      address2: contact.address2,
      city: contact.city,
      state: contact.state,
      zip: contact.zip
    };
    return selectContact;
  };

  const preview = async () => {
    if (hasUnsavedProductType || isSavingProductType) return;
    setPreview({ previewMode: true, index } as Preview);
    const payload = {
      orderNumber,
      vendor: convertToSelectContact(vendor),
      searchTypes: searches.map((s) => s.searchType),
      notes,
      dueDate: searchSections[index].dueDate,
      sellers: order?.contacts.filter((c) => c.type === "Seller").map((c) => c.name),
      buyers: order?.contacts.filter((c) => c.type === "Buyer/Borrower").map((c) => c.name),
      nameSearches: order?.contacts
        .filter((c) => OtherNameSearchTypes.some((t) => c.otherType === t))
        .map((c) => c.name),
      includedPropertyIds: order?.properties.map((p) => p.id)
    };
    logEvent("Order Searches", "Preview", JSON.stringify(payload, null, 2));
    const { data } = await axios({
      method: "post",
      url: `/proxy/api/ordersearches/preview`,
      data: payload
    });
    setPreview({
      previewMode: true,
      index,
      base64Document: data.base64Document
    });
  };

  const getAndSetIntelStateVendors = async () => {
    const state = order?.state;
    const { data } = await axios.get(`/api/orderSearches/getSearcherLookupCodesByState?state=${state}`);
    setIntelStateVendors(data);
  };

  const getAndSetIntelSearchGuidelineNotes = async () => {
    const state = order?.state;
    const { data } = await axios.get(`/api/orderSearches/getSearchGuidelineNotesByState?state=${state}`);
    setIntelSearchGuidelineNotes(data);
  };

  const getAndSetIntelSearchGuidelines = async () => {
    if (!underwriter) return;
    const state = order?.state;
    const { data } = await axios.get<IntelSearchGuideline[]>(
      `/api/orderSearches/getSearchGuidelinesByState?state=${state}`
    );
    // Checking both ways if includes underwriter name (Underwriter on order vs. from Intel)
    const underwritersGuidelines =
      data.find((d) => d.underwriter.name.includes(underwriter) || underwriter?.includes(d.underwriter.name)) || null;
    setIntelUnderwriterGuidelines(underwritersGuidelines);
  };

  useEffect(() => {
    if (!order?.state) return;
    getAndSetIntelStateVendors();
    getAndSetIntelSearchGuidelineNotes();
    getAndSetIntelSearchGuidelines();
  }, [order?.state]);

  const previewable = searchSections[index].vendor && searchSections[index].dueDate;

  return (
    <Card
      sx={
        isMobile
          ? {
              // Unwrapped on mobile: a card border around a screen-wide form is a
              // second box inside the screen's own edges.
              marginTop: 1,
              marginBottom: 2,
              padding: 0,
              boxShadow: "none",
              borderRadius: 0,
              backgroundColor: "transparent"
            }
          : { marginTop: 4, marginBottom: 4, padding: 2 }
      }
    >
      <Container
        sx={{
          marginTop: { xs: 0, md: 4 },
          marginBottom: { xs: 0, md: 4 },
          px: { xs: 0, md: 3 }
        }}
      >
        {!sentEmail && (
          <>
            {(intelSearchGuidelineNotes.length > 0 || intelUnderwriterGuidelines) && (
              <Stack direction="column" spacing={2} marginBottom={{ xs: 3, md: 6 }}>
                <Typography variant="h5">{order?.state} Guidelines</Typography>
                {intelSearchGuidelineNotes.map((n) => (
                  <Card key={n.id}>
                    <CardContent>
                      <Typography variant="subtitle2">{n.subject}</Typography>
                      <Typography variant="body2" whiteSpace="pre-line">
                        {n.note}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
                {intelUnderwriterGuidelines && <SearchGuidelinesTable guideline={intelUnderwriterGuidelines} />}
              </Stack>
            )}
            <Grid container justifyContent="space-between" spacing={{ xs: 2, md: 0 }}>
              <Grid item xs={12} md={8}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  justifyContent="flex-end"
                  alignItems={{ xs: "flex-start", md: "center" }}
                  spacing={2}
                >
                  <Box width="100%">
                    {onlyIntelStateVendors && (
                      <Autocomplete
                        value={vendor}
                        renderInput={(params) => <TextField {...params} label="Vendor" placeholder="Search Vendor" />}
                        options={intelStateVendors}
                        getOptionLabel={(option) => (option.isIndividual ? option.name : option.company) || ""}
                        onChange={(e, value, reason) => {
                          setVendor(index, value || ({} as Contact));
                        }}
                        renderOption={(props, option) => <ContactOption option={option} props={props} />}
                        PopperComponent={(props) => (
                          <Popper {...props} style={{ width: 650 }} placement="bottom-start" />
                        )}
                      />
                    )}
                    {!onlyIntelStateVendors && (
                      <PhonebookContactAutocomplete
                        contact={vendor}
                        setContact={(value: any) => {
                          setVendor(index, value || ({} as Contact));
                        }}
                        apiOptions={{ isOnlySearchContacts: true }}
                        textboxLabel="Vendor"
                        textboxPlaceholder="Search Vendor"
                      />
                    )}
                  </Box>
                  <FormControlLabel
                    sx={{ width: { xs: "auto", md: 705 } }}
                    control={
                      <Switch
                        defaultChecked={onlyIntelStateVendors}
                        onChange={() => setOnlyIntelStateVendors(!onlyIntelStateVendors)}
                      />
                    }
                    label="Filter to searchers in Intel for this state"
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} md={3}>
                <DesktopDatePicker
                  label="Due Date"
                  format="MM/dd/yyyy"
                  value={searchSections[index].dueDate}
                  onChange={(value: Date | null) => {
                    const nextState = produce(searchSections, (draftState) => {
                      draftState[index].dueDate = value;
                    });
                    setSearchSections(nextState);
                  }}
                  slotProps={{ textField: { size: "small", fullWidth: true } }}
                />
              </Grid>
            </Grid>
            <Grid container marginTop={{ xs: 2, md: 4 }} spacing={2}>
              {order?.properties && order?.properties.length > 0 && (
                <Grid item xs={12} md={4}>
                  <Properties />
                </Grid>
              )}
              {order?.contacts && order?.contacts.length > 0 && (
                <Grid item xs={12} md={4}>
                  <Names />
                </Grid>
              )}
              <Grid item xs={12} md={4}>
                <Attachments />
              </Grid>
            </Grid>
            <OrderProductType />
            <SearchItems index={index} />
            <Grid container marginTop={2}>
              <Grid item xs={4}></Grid>
            </Grid>
            <Grid container justifyContent="flex-end">
              <Grid item>
                <Button
                  sx={{ marginRight: 1 }}
                  color="primary"
                  size="small"
                  variant="outlined"
                  onClick={preview}
                  disabled={!previewable || !order || hasUnsavedProductType || isSavingProductType}
                >
                  Preview
                </Button>
              </Grid>
            </Grid>
          </>
        )}
        {sentEmail && <SentSearchEmailAlert />}
      </Container>
    </Card>
  );
}
